import React, { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { Heart, MessageSquare, MessageCircle, Euro, MapPin, Star, Flag, ChevronLeft, ChevronRight, Languages, BadgeCheck, Calendar, Truck, Phone, Mail, Globe, Copy, Umbrella } from "lucide-react";
import { useI18n } from "../i18n/I18nContext";
import { useAuth } from "../context/AuthContext";
import api, { fileUrl, formatApiError } from "../lib/api";
import { useSeo } from "../lib/useSeo";
import { formatPrice, timeAgo } from "../lib/format";
import { ISLAND_NAME } from "../lib/data";
import { Button } from "../components/ui/button";
import { Avatar, AvatarFallback, AvatarImage } from "../components/ui/avatar";
import { OfferModal, ReportModal } from "../components/modals";
import { toast } from "sonner";

export default function ListingDetail() {
  const { id } = useParams();
  const { t, lang } = useI18n();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [l, setL] = useState(null);
  const [idx, setIdx] = useState(0);
  const [fav, setFav] = useState(false);
  const [offerOpen, setOfferOpen] = useState(false);
  const [reportOpen, setReportOpen] = useState(false);
  const [translated, setTranslated] = useState(null);
  const [translating, setTranslating] = useState(false);
  const [trackingInput, setTrackingInput] = useState("");
  const [savingTracking, setSavingTracking] = useState(false);

  const load = () => api.get(`/listings/${id}`).then(({ data }) => { setL(data); setFav(data.is_favorite); }).catch(() => {});
  useEffect(() => { load(); setTranslated(null); }, [id]);

  const firstPhoto = l?.photos?.length ? l.photos[0] : null;
  useSeo({
    title: l ? `${l.title} - Canarias Market` : "Canarias Market",
    description: l ? (l.description || "").slice(0, 150) : "",
    image: firstPhoto ? fileUrl(firstPhoto) : undefined,
    type: "product",
  });

  if (!l) return <div className="p-10 text-center text-slate-400">{t("common.loading")}</div>;
  const photos = l.photos?.length ? l.photos : [null];
  const isOwn = user && user.id === l.seller_id;

  const requireAuth = () => { if (!user) { navigate("/auth"); return false; } return true; };

  const toggleFav = async () => {
    if (!requireAuth()) return;
    const { data } = await api.post(`/favorites/${l.id}`);
    setFav(data.favorite);
    toast.success(data.favorite ? t("favorites.added") : t("favorites.removed"));
  };

  const startChat = async () => {
    if (!requireAuth()) return;
    try {
      const { data } = await api.post("/conversations/open", { listing_id: l.id, receiver_id: l.seller_id });
      navigate(data?.conversation_id ? `/messages?conv=${encodeURIComponent(data.conversation_id)}` : "/messages");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const saveTracking = async () => {
    const code = trackingInput.trim();
    if (!code) return;
    setSavingTracking(true);
    try {
      await api.put(`/listings/${id}`, { tracking_number: code });
      setL((p) => ({ ...p, tracking_number: code }));
      setTrackingInput("");
      toast.success(t("sell.trackingSaved"));
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSavingTracking(false); }
  };

  const productUrl = typeof window !== "undefined" ? window.location.href : "";
  const openWhatsApp = () => {
    const num = (l.seller?.whatsapp_number || "").replace(/[^0-9]/g, "");
    if (!num) return;
    let photo = l.photos?.[0] ? fileUrl(l.photos[0]) : "";
    if (photo && !/^https?:\/\//i.test(photo)) photo = window.location.origin + photo;
    const lines = [];
    if (photo) lines.push(photo);
    lines.push(`${l.title} — ${formatPrice(l.price, lang)}`);
    lines.push(productUrl);
    lines.push("Me interesa este producto; ¿está disponible?");
    const msg = encodeURIComponent(lines.join("\n"));
    window.open(`https://wa.me/${num}?text=${msg}`, "_blank");
  };

  const translate = async () => {
    if (!requireAuth()) return;
    if (translated) { setTranslated(null); return; }
    setTranslating(true);
    try {
      const { data } = await api.post("/translate", { text: l.description, target_lang: lang });
      setTranslated(data.translation);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setTranslating(false); }
  };

  return (
    <div className="px-4 sm:px-8 py-6">
      <button onClick={() => navigate(-1)} className="flex items-center gap-1 text-sm text-slate-500 mb-4"><ChevronLeft className="h-4 w-4" />{t("common.back")}</button>
      <div className="grid lg:grid-cols-2 gap-8">
        {/* Gallery */}
        <div>
          <div className="relative aspect-square rounded-3xl overflow-hidden bg-slate-100">
            {photos[idx] ? <img src={fileUrl(photos[idx])} alt={l.title} className="w-full h-full object-cover" /> : <div className="w-full h-full grid place-items-center text-slate-300">{t("listing.noPhotos")}</div>}
            {l.is_boosted && <span className="absolute top-3 left-3 font-mono-badge text-[11px] px-3 py-1 rounded-full bg-[#FF9F1C] text-white">★ {t("home.featured")}</span>}
            {l.status === "vendido" && <span data-testid="detail-sold-badge" className="absolute top-3 right-3 font-mono-badge text-[12px] px-3 py-1 rounded-full bg-rose-600 text-white shadow">{t("listing.sold")}</span>}
            {photos.length > 1 && (
              <>
                <button onClick={() => setIdx((i) => (i - 1 + photos.length) % photos.length)} className="absolute left-2 top-1/2 -translate-y-1/2 h-9 w-9 grid place-items-center rounded-full bg-white/90 shadow"><ChevronLeft className="h-5 w-5" /></button>
                <button onClick={() => setIdx((i) => (i + 1) % photos.length)} className="absolute right-2 top-1/2 -translate-y-1/2 h-9 w-9 grid place-items-center rounded-full bg-white/90 shadow"><ChevronRight className="h-5 w-5" /></button>
              </>
            )}
          </div>
          {photos.length > 1 && (
            <div className="flex gap-2 mt-3 overflow-x-auto no-scrollbar">
              {photos.map((p, i) => (
                <button key={i} onClick={() => setIdx(i)} className={`h-16 w-16 shrink-0 rounded-xl overflow-hidden border-2 ${i === idx ? "border-[#008080]" : "border-transparent"}`}>
                  {p ? <img src={fileUrl(p)} alt="" className="w-full h-full object-cover" /> : <div className="w-full h-full bg-slate-100" />}
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Info */}
        <div>
          <div className="flex items-start justify-between gap-3">
            <h1 className="font-display font-extrabold text-2xl sm:text-3xl text-slate-900">{l.title}</h1>
            <button data-testid="detail-fav-btn" onClick={toggleFav} className="h-11 w-11 grid place-items-center rounded-full border border-slate-200 hover:bg-slate-50 shrink-0">
              <Heart className={`h-5 w-5 ${fav ? "fill-rose-500 text-rose-500" : "text-slate-600"}`} />
            </button>
          </div>
          <p className="font-display font-extrabold text-3xl text-[#008080] mt-2">{formatPrice(l.price, lang)}</p>

          {l.ref && (
            <button data-testid="listing-ref" onClick={() => { navigator.clipboard?.writeText(l.ref); toast.success(t("listing.refCopied")); }}
              className="mt-2 inline-flex items-center gap-1.5 text-xs font-semibold text-slate-500 bg-slate-100 hover:bg-slate-200 transition-colors rounded-full px-2.5 py-1"
              title={t("listing.refCopy")}>
              <span className="text-slate-400">{t("listing.id")}:</span>
              <span className="font-mono tracking-wide text-slate-700">{l.ref}</span>
              <Copy className="h-3 w-3" />
            </button>
          )}

          <div className="flex flex-wrap gap-2 mt-4">
            <span className="px-3 py-1 rounded-full bg-slate-100 text-sm text-slate-700">{t(`conditions.${l.condition}`)}</span>
            <span className="px-3 py-1 rounded-full bg-slate-100 text-sm text-slate-700 flex items-center gap-1"><MapPin className="h-3.5 w-3.5" />{l.municipality}, {ISLAND_NAME[l.island]}</span>
          </div>
          <div className="flex items-center gap-4 mt-3 text-xs text-slate-400">
            <span className="flex items-center gap-1"><Calendar className="h-3.5 w-3.5" />{timeAgo(l.created_at, lang)}</span>
            <span>{l.views} {t("listing.views")}</span>
          </div>

          <div className="mt-4 p-3 rounded-xl bg-teal-50/60 border border-teal-100 text-sm text-slate-600 space-y-2">
            <p className="flex items-start gap-2"><Truck className="h-4 w-4 text-[#008080] mt-0.5 shrink-0" /><span>{t("listing.deliveryCourier")}</span></p>
            <p className="flex items-start gap-2"><MapPin className="h-4 w-4 text-[#008080] mt-0.5 shrink-0" /><span>{t("listing.deliveryPickup")}</span></p>
            {l.tracking_number && (
              <p data-testid="listing-tracking" className="flex items-center gap-2 text-xs bg-white rounded-lg border border-slate-200 px-2.5 py-1.5 text-slate-700">
                <Truck className="h-3.5 w-3.5 text-emerald-600" />{t("listing.trackingNumber")}: <span className="font-mono font-semibold">{l.tracking_number}</span>
              </p>
            )}
            {isOwn && (
              <div className="flex items-center gap-2 pt-1">
                <input data-testid="own-tracking-input" value={trackingInput} onChange={(e) => setTrackingInput(e.target.value)}
                  placeholder={l.tracking_number ? t("listing.trackingUpdate") : t("sell.trackingNumber")}
                  className="flex-1 h-9 rounded-lg border border-slate-200 px-2.5 text-sm" />
                <Button data-testid="own-tracking-save" onClick={saveTracking} disabled={savingTracking || !trackingInput.trim()} className="h-9 bg-[#008080] hover:bg-[#006666] text-xs">{t("common.save")}</Button>
              </div>
            )}
          </div>

          {/* CTAs */}
          {!isOwn && (
            l.status === "vendido" ? (
              <div data-testid="sold-notice" className="mt-6 p-4 rounded-2xl bg-rose-50 border border-rose-200 text-rose-700 text-sm font-medium text-center">
                {t("listing.sold")}
              </div>
            ) : l.seller_on_vacation ? (
              <div data-testid="vacation-notice" className="mt-6 p-4 rounded-2xl bg-amber-50 border border-amber-200 text-amber-800 text-sm">
                <p className="flex items-start gap-2 font-medium"><Umbrella className="h-5 w-5 shrink-0" />{t("listing.vacationMessage")}</p>
                <Button data-testid="vacation-message-btn" onClick={() => startChat()} variant="outline" className="mt-3 h-10 w-full border-amber-400 text-amber-700 hover:bg-amber-100"><MessageSquare className="h-4 w-4 mr-1" />{t("listing.message")}</Button>
              </div>
            ) : (
              <div className="grid grid-cols-2 gap-3 mt-6">
                {l.seller?.whatsapp_number && (
                  <Button data-testid="whatsapp-btn" onClick={openWhatsApp} className="h-11 bg-emerald-500 hover:bg-emerald-600 text-white col-span-2"><MessageCircle className="h-4 w-4 mr-1" />{t("listing.whatsapp")}</Button>
                )}
                <Button data-testid="message-btn" onClick={() => startChat()} variant="outline" className="h-11 col-span-2"><MessageSquare className="h-4 w-4 mr-1" />{t("listing.message")}</Button>
                <Button data-testid="make-offer-btn" onClick={() => { if (requireAuth()) setOfferOpen(true); }} className="h-11 col-span-2 bg-[#FF9F1C] hover:bg-[#e88f13] text-white font-semibold"><Euro className="h-4 w-4 mr-1" />{t("listing.makeOffer")}</Button>
              </div>
            )
          )}
          {isOwn && <Link to="/dashboard"><Button className="w-full h-11 mt-6 bg-[#0F2537]">{t("dashboard.title")}</Button></Link>}

          {/* Description */}
          <div className="mt-8">
            <div className="flex items-center justify-between mb-2">
              <h3 className="font-display font-bold text-lg">{t("listing.description")}</h3>
              {l.description && (
                <button data-testid="translate-btn" onClick={translate} disabled={translating} className="flex items-center gap-1 text-sm text-[#008080] font-medium">
                  <Languages className="h-4 w-4" />{translated ? t("listing.original") : translating ? t("common.loading") : t("listing.translate")}
                </button>
              )}
            </div>
            <p className="text-slate-600 whitespace-pre-line leading-relaxed">{translated || l.description}</p>
          </div>

          {/* Seller card */}
          {l.seller && (
            <Link to={`/user/${l.seller.id}`} data-testid="seller-card" className="flex items-center gap-3 mt-8 p-4 rounded-2xl bg-white border border-slate-200 hover:border-teal-300 transition-colors">
              <Avatar className="h-12 w-12"><AvatarImage src={fileUrl(l.seller.profile_photo)} /><AvatarFallback className="bg-teal-100 text-teal-700">{l.seller.name?.[0]}</AvatarFallback></Avatar>
              <div className="flex-1">
                <p className="font-semibold flex items-center gap-1">{l.seller.is_professional ? l.seller.business_name || l.seller.name : l.seller.name}
                  {l.seller.is_professional && <BadgeCheck className="h-4 w-4 text-[#008080]" />}</p>
                <p className="text-xs text-slate-500 flex items-center gap-1">
                  {l.seller.rating > 0 && <><Star className="h-3 w-3 fill-amber-400 text-amber-400" />{l.seller.rating} · {l.seller.review_count} {t("listing.reviews")}</>}
                </p>
              </div>
              <span className="text-sm text-[#008080] font-medium">{t("common.viewProfile")}</span>
            </Link>
          )}

          {/* Business direct contact (deroga: professionals expose public contacts) */}
          {l.seller?.is_professional && (l.seller.phone || l.seller.business_email || l.seller.business_address || l.seller.website) && (
            <div data-testid="business-contact-card" className="mt-3 p-4 rounded-2xl bg-teal-50 border border-teal-100 space-y-2">
              <p className="font-semibold text-sm text-[#0F2537] flex items-center gap-1"><BadgeCheck className="h-4 w-4 text-[#008080]" />{t("listing.directContact")}</p>
              {l.seller.phone && <a href={`tel:${l.seller.phone}`} data-testid="biz-contact-phone" className="text-sm text-teal-800 flex items-center gap-2"><Phone className="h-3.5 w-3.5" />{l.seller.phone}</a>}
              {l.seller.business_email && <a href={`mailto:${l.seller.business_email}`} data-testid="biz-contact-email" className="text-sm text-teal-800 flex items-center gap-2"><Mail className="h-3.5 w-3.5" />{l.seller.business_email}</a>}
              {l.seller.business_address && <p className="text-sm text-teal-800 flex items-center gap-2"><MapPin className="h-3.5 w-3.5" />{l.seller.business_address}</p>}
              {l.seller.website && <a href={l.seller.website.startsWith("http") ? l.seller.website : `https://${l.seller.website}`} target="_blank" rel="noreferrer" data-testid="biz-contact-web" className="text-sm text-teal-800 flex items-center gap-2"><Globe className="h-3.5 w-3.5" />{l.seller.website}</a>}
            </div>
          )}

          {/* Reviews */}
          {l.seller_reviews?.length > 0 && (
            <div className="mt-6">
              <h3 className="font-display font-bold text-lg mb-3">{t("listing.reviews")}</h3>
              <div className="space-y-3">
                {l.seller_reviews.slice(0, 3).map((r) => (
                  <div key={r.id} className="p-3 rounded-xl bg-white border border-slate-200">
                    <div className="flex items-center gap-2">
                      <span className="flex">{Array.from({ length: 5 }).map((_, i) => <Star key={i} className={`h-3.5 w-3.5 ${i < r.rating ? "fill-amber-400 text-amber-400" : "text-slate-200"}`} />)}</span>
                      <span className="text-xs text-slate-500">{r.reviewer_name}</span>
                    </div>
                    {r.comment && <p className="text-sm text-slate-600 mt-1">{r.comment}</p>}
                  </div>
                ))}
              </div>
            </div>
          )}

          {!isOwn && (
            <button data-testid="report-btn" onClick={() => requireAuth() && setReportOpen(true)} className="flex items-center gap-1 text-xs text-slate-400 mt-6 hover:text-rose-500">
              <Flag className="h-3.5 w-3.5" />{t("listing.report")}
            </button>
          )}
        </div>
      </div>

      <OfferModal open={offerOpen} onOpenChange={setOfferOpen} listing={l} onDone={() => navigate("/messages")} />
      <ReportModal open={reportOpen} onOpenChange={setReportOpen} listing={l} />
    </div>
  );
}
